/**
 * Banner schedules are wall-clock times with no offset ("2026-11-02T09:00:00").
 * They're read in Pacific time, or in each viewer's own time zone when the
 * banner uses local time. Readings stay text: a Date in the browser's zone would
 * shift one that falls in the browser's own DST gap. `zonedInstant` turns a
 * reading into a real moment.
 */

export const PACIFIC_TIME_ZONE = 'America/Los_Angeles';

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

const pad = (value: number) => value.toString().padStart(2, '0');

/** The wall-clock reading of a Date's local fields. */
export const dateToWallClock = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

/** Milliseconds `timeZone` is ahead of UTC at `instant`. */
const zoneOffset = (instant: number, timeZone: string) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(instant))
      .map(({type, value}) => [type, Number(value)])
  );
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return asUtc - Math.floor(instant / 1000) * 1000;
};

/** The moment a wall-clock reading happens in `timeZone`. */
export const zonedInstant = (value: string, timeZone: string) => {
  const match = WALL_CLOCK.exec(value);
  if (!match) {
    return new Date(NaN);
  }
  const [, y, mo, d, h, mi, s] = match;
  const asUtc = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s ?? 0)
  );
  // Correct once for the offset at the guess, then again in case that crossed a DST change.
  const first = asUtc - zoneOffset(asUtc, timeZone);
  return new Date(asUtc - zoneOffset(first, timeZone));
};

export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export type BannerStatus = 'scheduled' | 'live' | 'ended';

/**
 * Where a banner is in its schedule. Local-time banners go live at a different
 * moment in each zone; this uses the admin's own.
 */
export const bannerStatus = (
  banner: {starts_at: string; ends_at: string; use_local_time: boolean},
  now = new Date(),
  localTimeZone = browserTimeZone()
): BannerStatus => {
  const timeZone = banner.use_local_time ? localTimeZone : PACIFIC_TIME_ZONE;
  if (now < zonedInstant(banner.starts_at, timeZone)) {
    return 'scheduled';
  }
  return now < zonedInstant(banner.ends_at, timeZone) ? 'live' : 'ended';
};

/** A reading as text. Formatted in UTC so the browser's own DST changes can't shift it. */
const formatReading = (value: string) =>
  zonedInstant(value, 'UTC').toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** Whether text is a whole wall-clock reading. */
export const isWallClock = (value: string) => WALL_CLOCK.test(value);

/** The reading's calendar day at noon, for the date picker. Noon is never in a DST gap. */
export const readingDay = (value: string) => {
  const match = WALL_CLOCK.exec(value);
  return match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)
    : new Date(NaN);
};

/** "Nov 2, 2026, 9:00 AM – Nov 9, 2026, 9:00 AM", as written, without converting zones. */
export const formatSchedule = (startsAt: string, endsAt: string) =>
  `${formatReading(startsAt)} – ${formatReading(endsAt)}`;

export const scheduleZoneLabel = (useLocalTime: boolean) =>
  useLocalTime ? "Each viewer's local time" : 'Pacific time';
